/* ──────────────────────────────────────────────────────────────
   Imports/index.tsx · Live bulk upsert (sk-catalog-api POST /import)
   Paste / edit a JSON array of rows and run a real 207-multi-status import.
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TextInput, ActivityIndicator, Platform, ViewStyle } from 'react-native';

import { useTheme } from '../context/themeContext';
import { Icon, Button, Chip, AppDropdown, Empty, useToast } from '../components/ui';
import { CONTENT } from '../lib/responsive';
import CommonInput from '../components/commonInput';
import { getItemTypes } from '../services/catalog/itemType';
import { importItems, ImportResponse } from '../services/catalog/import';
import { createItem } from '../services/catalog/item';
import { handleApiError } from '../utils/errorHandler';
import { showSuccess } from '../utils/successHandler';
import type { ItemType } from '../schemas/catalog/itemType';

export default function ImportsScreen() {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;
  const toast = useToast();

  const [types, setTypes] = useState<ItemType[]>([]);
  const [typeId, setTypeId] = useState('');
  const [json, setJson] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ImportResponse | null>(null);
  // 'rows' → bulk upsert (POST /import). 'items' → full item objects, each
  // created via POST /items.
  const [mode, setMode] = useState<'rows' | 'items'>('rows');

  useEffect(() => {
    (async () => {
      try {
        const res = await getItemTypes();
        setTypes(res.items ?? []);
        if (res.items?.[0]) setTypeId(res.items[0].id);
      } catch (err) { handleApiError(err, 'Could not load item types'); }
    })();
  }, []);

  // Seed an example payload for the current mode once a type is chosen.
  const seedExample = useCallback(() => {
    if (!typeId) return;
    const example = mode === 'rows'
      ? [
          { skuOrReference: 'IMP-001', itemTypeId: typeId, fields: [{ fieldKey: 'title', value: 'Imported item 1' }] },
          { skuOrReference: 'IMP-002', itemTypeId: typeId, fields: [{ fieldKey: 'title', value: 'Imported item 2' }] },
        ]
      : [
          {
            itemTypeId: typeId,
            currentState: 'DRAFT',
            skuOrReference: 'NEW-001',
            fields: [{ fieldKey: 'title', value: 'New item 1' }],
          },
        ];
    setJson(JSON.stringify(example, null, 2));
  }, [typeId, mode]);

  const run = async () => {
    let rows: any[];
    try {
      rows = JSON.parse(json);
      if (!Array.isArray(rows) || rows.length === 0) throw new Error('Provide a non-empty JSON array.');
    } catch (e: any) {
      toast.error('Invalid JSON', e?.message ?? 'Could not parse the JSON array.');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      if (mode === 'rows') {
        // Bulk upsert by SKU — POST /import. Inject the selected item type for
        // any row that didn't specify one (itemTypeId is required per row).
        const prepared = rows.map((r) => ({ ...r, itemTypeId: r.itemTypeId || typeId }));
        const res = await importItems(prepared);
        setResult(res);
        const ok = (res.created ?? 0) + (res.updated ?? 0);
        showSuccess({}, 'Import complete', `${ok} upserted · ${res.errors?.length ?? 0} failed.`);
      } else {
        // Full item JSON — create each via POST /items, then aggregate into the
        // same result shape so the summary card renders uniformly.
        let created = 0;
        const errors: ImportResponse['errors'] = [];
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          try {
            await createItem({ itemTypeId: row.itemTypeId || typeId, ...row });
            created++;
          } catch (err: any) {
            errors.push({
              index: i,
              skuOrReference: row.skuOrReference,
              error: err?.response?.data?.error?.message || err?.message || 'Failed to create',
            });
          }
        }
        setResult({ total: rows.length, created, updated: 0, skipped: 0, errors });
        showSuccess({}, 'Import complete', `${created} created · ${errors.length} failed.`);
      }
    } catch (err) {
      handleApiError(err, 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  const card: ViewStyle = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, ...(shadows.sm as ViewStyle) };
  const mono = { fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink, ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}) };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64, maxWidth: CONTENT.default }}>
      <View style={{ marginBottom: 20 }}>
        <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>Imports</Text>
        <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: font.sans, marginTop: 4 }}>
          {mode === 'rows'
            ? 'Bulk upsert up to 5,000 items by SKU/reference. Returns per-row results (207 Multi-Status).'
            : 'Paste full item JSON objects — each is created via the items endpoint (POST /items).'}
        </Text>
      </View>

      <View style={[card, { padding: 16, marginBottom: 16, gap: 12 }]}>
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Format toggle — switches the endpoint used. */}
          <View style={{ flexDirection: 'row', borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, overflow: 'hidden' }}>
            {([['rows', 'Bulk rows'], ['items', 'Full item JSON']] as const).map(([m, label]) => (
              <Text
                key={m}
                onPress={() => { setMode(m); setResult(null); }}
                style={{
                  fontSize: fontSize.sm,
                  fontFamily: font.sans,
                  fontWeight: fontWeight.medium,
                  paddingVertical: 9,
                  paddingHorizontal: 14,
                  color: mode === m ? colors.paper : colors.ink2,
                  backgroundColor: mode === m ? colors.ink : colors.card,
                  ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
                }}
              >
                {label}
              </Text>
            ))}
          </View>
          <View style={{ width: 200 }}>
            <AppDropdown compact placeholder="Item type" value={typeId} items={types.map(t => ({ label: t.name, value: t.id }))} onSelect={setTypeId} isDark={isDark} fieldStyle={{ width: 200 }} />
          </View>
          <Button size="sm" icon="sparkles" onPress={seedExample}>Insert example</Button>
        </View>

        <View style={{ gap: 6 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>
            {mode === 'rows' ? 'Rows (JSON array)' : 'Items (JSON array of full item objects)'}
          </Text>
          <CommonInput
            value={json}
            onChangeText={setJson}
            placeholder={mode === 'rows'
              ? '[{ "skuOrReference": "IMP-001", "itemTypeId": "…", "fields": [{ "fieldKey": "title", "value": "…" }] }]'
              : '[{ "itemTypeId": "…", "currentState": "DRAFT", "skuOrReference": "NEW-001", "fields": [{ "fieldKey": "title", "value": "…" }] }]'}
            isDark={isDark}
            containerStyle={{ marginBottom: 0 }}
            inputProps={{ multiline: true, numberOfLines: 14, textAlignVertical: 'top', style: [mono, { minHeight: 240 }] }}
          />
        </View>

        <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
          <Button kind="accent" icon="import" disabled={running || !json.trim()} onPress={run}>
            {running ? 'Importing…' : 'Run import'}
          </Button>
        </View>
      </View>

      {result && (
        <View style={card}>
          <View style={{ padding: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Result</Text>
            <Chip tone="neutral">{result.total} total</Chip>
            <Chip tone="success">{result.created} created</Chip>
            <Chip tone="success">{result.updated} updated</Chip>
            {result.skipped > 0 && <Chip tone="neutral">{result.skipped} skipped</Chip>}
            {(result.errors?.length ?? 0) > 0 && <Chip tone="danger">{result.errors.length} failed</Chip>}
          </View>
          <View style={{ padding: 16, gap: 6 }}>
            {(result.errors ?? []).length === 0 ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>All rows imported successfully.</Text>
            ) : (result.errors).map((e, i) => (
              <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: i < (result.errors.length - 1) ? 1 : 0, borderBottomColor: colors.line }}>
                <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>{e.skuOrReference ?? `row ${e.index ?? i}`}</Text>
                <Chip tone="danger">{e.error}</Chip>
              </View>
            ))}
          </View>
        </View>
      )}
    </ScrollView>
  );
}
